import { HypnogramSample } from '../types/post';

/**
 * Calculate sleep duration from timeline data, excluding awake periods
 * @param sleepTimeline Array of hypnogram samples with timestamp and level
 * @param fallbackDuration Optional fallback duration in seconds if timeline is unavailable
 * @returns Sleep duration in seconds (excluding awake time)
 */
export const calcSleepFromTimeline = (
  sleepTimeline?: HypnogramSample[],
  fallbackDuration?: number
): number => {
  // Use fallback if timeline is not available or empty
  if (!sleepTimeline || sleepTimeline.length === 0) {
    return fallbackDuration || 0;
  }

  let totalSleepMinutes = 0;

  // Process consecutive sample pairs to calculate duration
  for (let i = 0; i < sleepTimeline.length - 1; i++) {
    const current = sleepTimeline[i];
    const next = sleepTimeline[i + 1];

    // Only count actual sleep states (exclude awake states)
    // Level 1 = Awake, so we skip those segments
    if (current.level !== 1) {
      const startTime = new Date(current.timestamp);
      const endTime = new Date(next.timestamp);
      const segmentDurationMinutes = (endTime.getTime() - startTime.getTime()) / (1000 * 60);
      totalSleepMinutes += segmentDurationMinutes;
    }
  }

  // Convert minutes to seconds and round
  return Math.round(totalSleepMinutes * 60);
};

/**
 * Format sleep duration for display
 * @param durationInSeconds Duration in seconds
 * @returns Formatted string like "7h 32m" or "45m"
 */
export const formatSleepDuration = (durationInSeconds: number): string => {
  if (durationInSeconds === 0) return "0m";

  let hours = Math.floor(durationInSeconds / 3600);
  let minutes = Math.round((durationInSeconds % 3600) / 60);
  if (minutes === 60) {
    hours += 1;
    minutes = 0;
  }

  if (hours === 0) {
    return `${minutes}m`;
  } else if (minutes === 0) {
    return `${hours}h`;
  } else {
    return `${hours}h ${minutes}m`;
  }
};

/**
 * Convert wellness log sleepTime value to duration in seconds
 * @param sleepTime Wellness log sleepTime value (1-7)
 * @returns Sleep duration in seconds
 */
export const calcSleepFromWellnessLog = (sleepTime: number): number => {
  // Mapping from wellness log sleepTime values to hours
  const sleepTimeToHours: Record<number, number> = {
    1: 3.5,     // "less than 4 hours" -> 3.5 hours
    2: 4.5,     // "4 to 5 hours" -> 4.5 hours
    3: 5.5,     // "5 to 6 hours" -> 5.5 hours
    4: 6.25,    // "6 to 6.5 hours" -> 6.25 hours
    5: 7,       // "6.5 to 7.5 hours" -> 7 hours
    6: 7.5,     // "7.5 to 8.5 hours" -> 7.5 hours (7h 30m as requested)
    7: 9,       // "8.5+ hours" -> 9 hours
  };

  const hours = sleepTimeToHours[sleepTime] || 0;
  return Math.round(hours * 3600); // Convert to seconds
};

/**
 * Calculate sleep duration from health logs using timeline when available
 * @param healthLogs Array of health logs
 * @returns Sleep duration in seconds from the most recent health log
 */
// export const calcSleepFromHealthLogs = (healthLogs: any[]): number => {
//   if (healthLogs.length === 0) return 0;
//   const latestHealthLog = healthLogs[healthLogs.length - 1];
//   return latestHealthLog ? latestHealthLog.sleepDuration : 0;
  
// };
export const calcSleepFromHealthLogs = (healthLogs: any[]): number => {
  if (healthLogs.length === 0) return 0;

  // Some days may still have multiple HealthLog docs from before the
  // backend dedup fix. Pick the one with the longest sleep duration.
  const longestLog = healthLogs.reduce((max, log) =>
    (log.sleepDuration || 0) > (max.sleepDuration || 0) ? log : max
  );

  return longestLog.sleepDuration || 0;
};

/**
 * Calculate sleep duration with fallback to wellness logs
 * @param healthLogs Array of health logs
 * @param wellnessLogs Array of wellness logs
 * @returns Sleep duration in seconds from health logs or wellness logs as fallback
 */
export const calcSleepWithWellnessFallback = (healthLogs: any[], wellnessLogs: any[]): number => {
  // First try to get sleep from health logs
  const healthSleep = calcSleepFromHealthLogs(healthLogs);
  // console.log("healthSleep in calcSleepWithWellnessFallback ", healthSleep)
  if (healthSleep > 0) return healthSleep;

  // Fallback to wellness logs if no health data
  if (wellnessLogs.length === 0) return 0;

  // Get the most recent wellness log with sleep data
  const latestWellnessLog = wellnessLogs[wellnessLogs.length - 1];
  if (!latestWellnessLog?.sleepTime) return 0;

  return calcSleepFromWellnessLog(latestWellnessLog.sleepTime);
};
